<template>
  <div
    class="fixed inset-y-0 left-0 z-50 w-64 bg-gradient-to-b from-slate-900 to-slate-800 shadow-xl transform transition-transform duration-300 ease-in-out lg:translate-x-0"
    :class="{ '-translate-x-full': !sidebarOpen }"
  >
    <div class="flex h-full flex-col">
      <!-- Logo -->
      <div class="flex h-16 items-center justify-center border-b border-slate-700 px-6">
        <div class="flex items-center space-x-3">
          <div
            class="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold text-lg shadow-lg"
          >
            X
          </div>
          <span class="text-xl font-bold text-white">Xpert LMS</span>
        </div>
      </div>

      <!-- Navigation -->
      <nav class="flex-1 space-y-2 px-4 py-6 overflow-y-auto">
        <!-- Website Dropdown -->
        <div class="space-y-1">
          <button
            @click="toggleWebsiteDropdown"
            :class="[
              websiteDropdownOpen
                ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                : 'text-slate-300 hover:bg-slate-700 hover:text-white',
              'group flex w-full items-center justify-between px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 transform hover:scale-105'
            ]"
          >
            <div class="flex items-center">
              <Globe
                :class="[
                  websiteDropdownOpen
                    ? 'text-white'
                    : 'text-slate-400 group-hover:text-white',
                  'mr-3 h-5 w-5 transition-colors duration-200'
                ]"
              />
              Website
            </div>
            <ChevronDown
              :class="[
                websiteDropdownOpen
                  ? 'text-white rotate-180'
                  : 'text-slate-400 group-hover:text-white',
                'h-4 w-4 transition-all duration-300'
              ]"
            />
          </button>

          <!-- Website Dropdown Content -->
          <Transition
            enter-active-class="transition-all duration-300 ease-out"
            enter-from-class="opacity-0 transform -translate-y-2 scale-95"
            enter-to-class="opacity-100 transform translate-y-0 scale-100"
            leave-active-class="transition-all duration-200 ease-in"
            leave-from-class="opacity-100 transform translate-y-0 scale-100"
            leave-to-class="opacity-0 transform -translate-y-2 scale-95"
          >
            <div v-if="websiteDropdownOpen" class="ml-4 space-y-1 border-l-2 border-slate-600 pl-4">
              <RouterLink
                v-for="item in websiteItems"
                :to="item.href"
                :key="item.name"
                @click.stop="selectWebsiteItem(item)"
                :class="[
                  isActiveItem(item)
                    ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-blue-300 border-l-2 border-blue-400'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50',
                  'group flex w-full items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 transform hover:scale-105 hover:translate-x-1'
                ]"
                custom
                v-slot="{ navigate, }"
              >
                <div
                  @click="navigate"
                  class="flex items-center w-full cursor-pointer"
                >
                  <component
                    :is="item.icon"
                    :class="[
                      isActiveItem(item)
                        ? 'text-blue-400'
                        : 'text-slate-500 group-hover:text-slate-300',
                      'mr-3 h-4 w-4 transition-colors duration-200'
                    ]"
                  />
                  {{ item.name }}
                  <div
                    v-if="isActiveItem(item)"
                    class="ml-auto h-2 w-2 rounded-full bg-blue-400 animate-pulse"
                  ></div>
                </div>
              </RouterLink>
            </div>
          </Transition>
        </div>

        <!-- Additional Navigation Items (if needed) -->
        <div class="pt-4 border-t border-slate-700">
          <button
            @click="handleSettings"
            class="group flex w-full items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 transform hover:scale-105 text-slate-300 hover:bg-slate-700 hover:text-white"
          >
            <Settings
              class="mr-3 h-5 w-5 transition-colors duration-200 text-slate-400 group-hover:text-white"
            />
            Settings
          </button>
        </div>
      </nav>

    </div>
  </div>

  <!-- Mobile Overlay -->
  <div
    v-if="sidebarOpen"
    class="fixed inset-0 z-40 backdrop-blur-[2px]"
    @click="$emit('toggle-sidebar')"
  ></div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import {
  Globe,
  ChevronDown,
  Navigation,
  Image,
  Newspaper,
  User,
  Calendar,
  Building,
  Layout,
  Settings
} from 'lucide-vue-next'
import { RouterLink } from 'vue-router'

defineProps({
  sidebarOpen: {
    type: Boolean,
    default: false
  }
})

defineEmits(['toggle-sidebar', 'navigate'])

const route = useRoute()
const websiteDropdownOpen = ref(false)
const selectedWebsiteItem = ref('')

const websiteItems = ref([
  {
    name: 'Navbar',
    icon: Navigation,
    href: '/dashboard/navbar'
  },
  {
    name: 'Carousel',
    icon: Image,
    href: '/dashboard/carousel'
  },
  {
    name: 'Latest News',
    icon: Newspaper,
    href: '/dashboard/latestnews'
  },
  {
    name: 'Principle',
    icon: User,
    href: '/dashboard/principle'
  },
  {
    name: 'Activities',
    icon: Calendar,
    href: '/dashboard/activities'
  },
  {
    name: 'Campuses',
    icon: Building,
    href: '/dashboard/campuses'
  },
  {
    name: 'Footer',
    icon: Layout,
    href: '/dashboard/footer'
  }
])

// Check if an item is active based on current route
const isActiveItem = (item) => {
  return route.path === item.href || selectedWebsiteItem.value === item.name
}

// Watch for route changes to update selected item
watch(() => route.path, (newPath) => {
  const activeItem = websiteItems.value.find(item => item.href === newPath)
  if (activeItem) {
    selectedWebsiteItem.value = activeItem.name
    // Keep dropdown open if we're on a website item
    websiteDropdownOpen.value = true
  }
}, { immediate: true })

const toggleWebsiteDropdown = () => {
  websiteDropdownOpen.value = !websiteDropdownOpen.value
}

const selectWebsiteItem = (item) => {
  selectedWebsiteItem.value = item.name
  // Keep dropdown open when selecting an item
  websiteDropdownOpen.value = true
  
  // Emit navigation event if needed
  console.log(`Selected: ${item.name}`)
}

const handleSettings = () => {
  console.log('Settings clicked')
}

// Initialize dropdown state based on current route
const initializeDropdownState = () => {
  const currentItem = websiteItems.value.find(item => item.href === route.path)
  if (currentItem) {
    selectedWebsiteItem.value = currentItem.name
    websiteDropdownOpen.value = true
  }
}

// Initialize on component mount
initializeDropdownState()
</script>

<style scoped>
/* Custom scrollbar for the navigation */
nav::-webkit-scrollbar {
  width: 4px;
}

nav::-webkit-scrollbar-track {
  background: rgba(51, 65, 85, 0.1);
}

nav::-webkit-scrollbar-thumb {
  background: rgba(100, 116, 139, 0.5);
  border-radius: 2px;
}

nav::-webkit-scrollbar-thumb:hover {
  background: rgba(100, 116, 139, 0.7);
}

/* Smooth animations for all interactive elements */
* {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}
</style>